import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase-config";
import { collection, getDocs, query, where, updateDoc, doc, getDoc } from "firebase/firestore";
import loginBg from '../assets/svg/login.svg';
import "./EmployeeDashboard.css";
import ReactModal from 'react-modal';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { parseISODate, getSunday } from '../utils/dateUtils';
import validationStamp from '../assets/validation.png';
import './EmployeeTimesheet.css';
import { uploadPdfToR2, generateSignedUrl } from '../services/r2Service';

// Fonction utilitaire pour gérer tous les formats de date
const formatDateSafely = (dateInput) => {
  if (!dateInput) return '';
  
  let date;
  
  // Si c'est déjà une string YYYY-MM-DD
  if (typeof dateInput === 'string') {
    try {
      date = parseISODate(dateInput);
    } catch (error) {
      console.warn('Format de date invalide:', dateInput);
      return '';
    }
  }
  // Si c'est un objet Date
  else if (dateInput instanceof Date) {
    date = dateInput;
  }
  // Si c'est un Timestamp Firebase
  else if (dateInput && typeof dateInput.toDate === 'function') {
    date = dateInput.toDate();
  }
  // Si c'est un Timestamp Firebase avec seconds
  else if (dateInput && typeof dateInput.seconds === 'number') {
    date = new Date(dateInput.seconds * 1000);
  }
  else {
    console.warn('Format de date non reconnu:', dateInput);
    return '';
  }
  
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const statusInfo = {
  waitingEmployer: { text: "En attente de validation Employeur", color: "rgb(229, 57, 53)" }, // ROUGE
  waitingSignature: { text: "En attente de signature Salarié", color: "rgb(255, 152, 0)" }, // ORANGE  
  signed: { text: "Signé", color: "rgb(67, 160, 71)" }, // VERT
};

const getWeekStatus = (week) => {
  if (week.signed) return 'signed';
  if (week.status === 'En attente de signature salarié' || week.status === 'En attente de signature Salarié') return 'waitingSignature';
  return 'waitingEmployer'; // Par défaut : en attente de validation employeur
};

// Fonction utilitaire pour obtenir un objet Date à partir de n'importe quel format
const toDateObj = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput === 'string') {
    try {
      return parseISODate(dateInput);
    } catch {
      return null;
    }
  }
  if (dateInput && typeof dateInput.toDate === 'function') return dateInput.toDate();
  if (dateInput && typeof dateInput.seconds === 'number') return new Date(dateInput.seconds * 1000);
  return null;
};

function EmployeeTimesheet() {
  const navigate = useNavigate();
  const [employeeName, setEmployeeName] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [weeks, setWeeks] = useState([]); // [{monthStart, monthEnd, monthName, year, sessions, absences, status, pdfUrl, signed}]
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signData, setSignData] = useState(null);
  const [comment, setComment] = useState("");
  const [sigCanvas, setSigCanvas] = useState(null);
  const [slider, setSlider] = useState(false);
  const [signed, setSigned] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sites, setSites] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (user) {
        // Récupérer les infos du salarié
        const empQuery = query(
          collection(db, "employees"),
          where("email", "==", user.email)
        );
        const empSnap = await getDocs(empQuery);
        if (!empSnap.empty) {
          const employeeData = empSnap.docs[0].data();
          setEmployeeName(`${employeeData.prenom} ${employeeData.nom}`);
          // Stocker les informations de l'entreprise
          setEmployeeData(employeeData);
        }
        // Récupérer les feuilles de temps mensuelles (workSessionsMonth)
        const wsq = query(collection(db, "workSessionsMonth"), where("employeeEmail", "==", user.email));
        const wsSnap = await getDocs(wsq);
        
        // Trier par date décroissante et garder seulement les 5 plus récentes
        const allWeeks = wsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => b.monthStart.localeCompare(a.monthStart));
        
        const weeksArr = allWeeks.slice(0, 5); // Limiter à 5 feuilles
        
        setWeeks(weeksArr);
        setLoading(false);
      }
    };
    fetchData();
  }, [signed]);

  // Charger les sites pour la gestion des zones
  useEffect(() => {
    const fetchSites = async () => {
      const snap = await getDocs(collection(db, "sites"));
      setSites(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchSites();
  }, []);

  // Charger les sessions du mois pour le popup
  useEffect(() => {
    if (!modalOpen || !modalData) return;
    // Correction : conversion en Date pour le mois EN HEURE LOCALE
    const monthStartDate = modalData.monthStart ? new Date(modalData.monthStart + 'T00:00:00') : null;
    const monthEndDate = modalData.monthEnd ? new Date(modalData.monthEnd + 'T23:59:59') : null;
    console.log('[DEBUG] Chargement sessions - Mois du', monthStartDate?.toISOString(), 'au', monthEndDate?.toISOString());

    const fetchSessions = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "workSessions"),
        where("employeeEmail", "==", user.email)
      );
      const snap = await getDocs(q);
      const allSessions = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filtrer sur le mois
      const filtered = allSessions.filter(s => {
        const st = s.startTime?.seconds ? new Date(s.startTime.seconds * 1000) : null;
        return st && st >= monthStartDate && st <= monthEndDate;
      });
      setSessions(filtered.sort((a, b) => (a.startTime?.seconds || 0) - (b.startTime?.seconds || 0)));
    };
    fetchSessions();
  }, [modalOpen, modalData]);

  // Charger les absences du salarié pour la période
  useEffect(() => {
    if (!modalOpen || !modalData) return;
    const fetchAbsences = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "absences"),
        where("employeeEmail", "==", user.email)
      );
      const snap = await getDocs(q);
      setAbsences(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchAbsences();
  }, [modalOpen, modalData]);

  // Fonction pour recharger les données fraîches d'une feuille de temps
  const reloadWeekData = async (weekId) => {
    try {
      const weekDoc = await getDoc(doc(db, "workSessionsMonth", weekId));
      if (weekDoc.exists()) {
        const freshData = { id: weekDoc.id, ...weekDoc.data() };
        setWeeks(prev => prev.map(w => w.id === weekId ? freshData : w));
        if (modalData && modalData.id === weekId) {
          setModalData(freshData);
        }
        if (signData && signData.id === weekId) {
          setSignData(freshData);
        }
        return freshData;
      }
    } catch (error) {
      console.error("Erreur lors du rechargement des données:", error);
    }
    return null;
  };

  // Fonction utilitaire pour générer l'en-tête du PDF
  const generatePDFHeader = (docPdf, signData, employeeData) => {
    docPdf.setFontSize(20);
    docPdf.text('Feuille de temps', 14, 20);

    docPdf.setFontSize(13);
    docPdf.setFont("helvetica", "bold");
    docPdf.text(`Salarié : ${signData.employeeName || ''}`, 14, 32);

    docPdf.setFont("helvetica", "normal");
    docPdf.text(`Société : ${employeeData.entreprise || '-'}`, 14, 38);
    docPdf.text(`Siret : ${employeeData.siret || '-'}`, 14, 44);

    docPdf.setFont("helvetica", "bold");
    const monthStartDate = toDateObj(signData.monthStart);
    const monthEndDate = toDateObj(signData.monthEnd);
    docPdf.text(`Période : ${signData.monthName || ''} ${signData.year || ''}`, 14, 54);
    docPdf.setFont("helvetica", "normal");
    docPdf.setFontSize(9);
    docPdf.text(`(du ${monthStartDate ? monthStartDate.toLocaleDateString('fr-FR') : ''} au ${monthEndDate ? monthEndDate.toLocaleDateString('fr-FR') : ''})`, 14, 59);

    return 68; // startY pour le premier tableau (ajusté pour la ligne supplémentaire)
  };

  // Fonction utilitaire pour générer la pagination et la mention légale
  const generatePDFFooter = (docPdf) => {
    const pageCount = docPdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      docPdf.setPage(i);
      docPdf.setFontSize(8);
      
      if (i === pageCount) {
        docPdf.text(
          'Fiche de temps à conserver légalement pendant 5 ans par les parties.',
          docPdf.internal.pageSize.width / 2,
          docPdf.internal.pageSize.height - 20,
          { align: 'center' }
        );
      }
      
      docPdf.text(
        `Page ${i} sur ${pageCount}`,
        docPdf.internal.pageSize.width / 2,
        docPdf.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
  };

  // Fonction utilitaire pour générer le tableau des sessions
  const generateSessionsTable = (docPdf, sessions, startY) => {
    const totalTravail = sessions.reduce((acc, s) => acc + (s.adjustedWorkTime != null ? s.adjustedWorkTime : (s.startTime?.seconds && s.endTime?.seconds ? Math.max(0, (s.endTime.seconds - s.startTime.seconds) * 1000 - (s.totalPauseTime || 0)) : 0)), 0);
    const totalPause = sessions.reduce((acc, s) => acc + (s.totalPauseTime || 0), 0);

    // Nouvelle fonction utilitaire pour choisir l'heure corrigée ou non
    const getSessionTime = (session, type) => {
      const field = type === 'start' ? (session.adjustedStartTime || session.startTime) : (session.adjustedEndTime || session.endTime);
      if (!field) return '';
      if (field.seconds !== undefined) {
        const date = new Date(field.seconds * 1000);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      } else if (typeof field === 'number') {
        const date = new Date(field * 1000);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      } else if (field.toDate) {
        const date = field.toDate();
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      }
      return '';
    };

    autoTable(docPdf, {
      startY: startY,
      head: [["Date", "Début", "Fin", "Temps de Travail", "Pause", "Zone Début", "Zone Fin"]],
      body: sessions.map(s => [
        s.startTime?.seconds ? new Date(s.startTime.seconds * 1000).toLocaleDateString('fr-FR') : '',
        getSessionTime(s, 'start'),
        getSessionTime(s, 'end'),
        getWorkDuration(s),
        getPauseDuration(s),
        getZone(s.startLocation),
        getZone(s.endLocation)
      ]),
      foot: [["Total", "", "", formatTime(totalTravail), formatTime(totalPause), "", ""]],
      theme: 'grid',
      styles: { fontSize: 10, fillColor: false },
      headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    return docPdf.lastAutoTable ? docPdf.lastAutoTable.finalY + 10 : startY + 10;
  };

  // Fonction utilitaire pour générer le bloc de signature
  const generateSignatureBlock = (docPdf, y, signData, isPreview = true, signatureDataUrl = null) => {
    // DEBUG
    console.log('[DEBUG] generateSignatureBlock isPreview:', isPreview);
    // Vérifier si on a assez d'espace pour le bloc de signature (environ 100 points de hauteur)
    if (y > docPdf.internal.pageSize.height - 120) {
      docPdf.addPage();
      y = 20;
    }

    // Bloc de signature salarié
    if (isPreview) {
      docPdf.setFontSize(12);
      docPdf.text("en attente de signature", 14, y);
      y += 5;
      docPdf.text("Le Salarié", 14, y);
      y += 5;
      const [prenom, ...nomParts] = (signData.employeeName || '').split(' ');
      const nom = nomParts.join(' ');
      docPdf.text(`${prenom} ${nom}`, 14, y);
      y += 5;
      docPdf.text("Le :", 14, y);
    } else {
      // Gérer la signature réelle pour le PDF final
      const signatureY = y; // Position de départ pour la signature
      const signatureHeight = 15; // Hauteur allouée pour la signature et un peu d'espace

      if (signatureDataUrl) {
        try {
          const sigWidth = 40; // Largeur fixe de 3.6cm (+20%)
          const sigHeight = 12; // Hauteur fixe de 1.44cm (+20%)
          // Ajouter la signature en haut du bloc
          docPdf.addImage(signatureDataUrl, 'PNG', 14, signatureY, sigWidth, sigHeight);
        } catch (error) {
          console.error("Erreur lors de l'ajout de la signature au PDF:", error);
          docPdf.text("Signature illisible", 14, signatureY);
        }
      }

      // Placer le texte sous l'espace de la signature
      const textY = signatureY + signatureHeight;
      docPdf.text("Le Salarié", 14, textY);
      y = textY + 5; // Mettre à jour y pour le texte suivant
      
      const [prenom, ...nomParts] = (signData.employeeName || '').split(' ');
      const nom = nomParts.join(' ');
      docPdf.text(`${prenom} ${nom}`, 14, y);
      y += 5;

      const now = new Date();
      const dateStr = now.toLocaleDateString('fr-FR');
      const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      docPdf.text(`Le : ${dateStr}, ${timeStr}`, 14, y);
    }

    y += 15; // Espace entre les blocs salarié et employeur

    // Toujours afficher le bloc employeur
    try {
      // Réduction de 15% de la taille du tampon
      const imgWidth = 20.4; // 24 * 0.85
      const imgHeight = 20.4; // 24 * 0.85
      docPdf.addImage(validationStamp, 'PNG', 14, y, imgWidth, imgHeight);
    } catch (error) {
      console.error("Erreur lors de l'ajout du tampon:", error);
    }
    y += 30;
    docPdf.text("L'Employeur", 14, y);
    y += 5;
    docPdf.text("Signature Digitale", 14, y);
    y += 5;
    // Afficher la date de validation si elle existe
    const adminDate = signData.adminValidatedAt ? 
      new Date(signData.adminValidatedAt.seconds ? signData.adminValidatedAt.seconds * 1000 : signData.adminValidatedAt) : 
      null;
    const adminDateStr = adminDate ? adminDate.toLocaleDateString('fr-FR') : '';
    const adminTimeStr = adminDate ? adminDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
    docPdf.text(`Le : ${adminDateStr ? `${adminDateStr}, ${adminTimeStr}` : ''}`, 14, y);
    return y + 10;
  };

  // Générer le PDF temporaire à l'ouverture du popup de signature
  useEffect(() => {
    if (signModalOpen && signData && employeeData) {
      const docPdf = new jsPDF();
      docPdf.setFont("helvetica", "normal");

      const startY = generatePDFHeader(docPdf, signData, employeeData);
      const sessions = signData.sessions || [];
      let y = generateSessionsTable(docPdf, sessions, startY);

      // Titre du tableau des absences
      docPdf.setFontSize(13);
      docPdf.text('Absences et Congés', 14, y);
      y += 6;

      // Tableau des absences
      autoTable(docPdf, {
        startY: y,
        head: [["Type", "Date début", "Début", "Date fin", "Fin"]],
        body: (signData.absences || []).map(a => [
          a.type || '-',
          a.startDate || '-',
          a.startPart || '-',
          a.endDate || '-',
          a.endPart || '-'
        ]),
        theme: 'grid',
        styles: { fontSize: 10, fillColor: false },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] }
      });

      y = docPdf.lastAutoTable ? docPdf.lastAutoTable.finalY + 20 : y + 30;

      // **ORDRE CORRIGÉ** : Commentaires d'abord
      if (comment || signData.comment) {
        docPdf.setFontSize(10);
        docPdf.setFont("helvetica", "bold");
        docPdf.text("Commentaires Salarié :", 14, y);
        y += 6;
        docPdf.setFont("helvetica", "normal");
        const commentText = comment || signData.comment || "";
        const lines = docPdf.splitTextToSize(commentText, 180);
        docPdf.text(lines, 14, y);
        y += (lines.length * 5) + 10;
      }

      if (signData.employerComment) {
        docPdf.setFontSize(10);
        docPdf.setFont("helvetica", "bold");
        docPdf.text("Commentaires Employeur :", 14, y);
        y += 6;
        docPdf.setFont("helvetica", "normal");
        const lines = docPdf.splitTextToSize(signData.employerComment, 180);
        docPdf.text(lines, 14, y);
        y += (lines.length * 5) + 10;
      }
      
      // Vérifier si on a besoin d'une nouvelle page AVANT le bloc signature
      if (y > docPdf.internal.pageSize.height - 120) {
        docPdf.addPage();
        y = 20;
      }
      
      // DEBUG: log avant la génération du bloc signature
      console.log('[DEBUG] useEffect PDF preview: appel generateSignatureBlock avec isPreview:true');
      // **ORDRE CORRIGÉ** : Bloc signature APRÈS (version finale avec signature)
      y = generateSignatureBlock(docPdf, y, signData, true, null);

      generatePDFFooter(docPdf);

      const pdfBlob = docPdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(url);
    } else {
      setPdfPreviewUrl("");
    }
  }, [signModalOpen, signData, employeeData]);

  // Fonctions utilitaires (reprendre la logique AdminDashboard)
  function formatTime(ms) {
    if (!ms || isNaN(ms)) return '00:00';
    const totalMinutes = Math.round(ms / 60000); // Arrondi à la minute la plus proche
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  function formatTimeHHMM(timestamp) {
    if (!timestamp) return '';
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.seconds !== undefined) {
      const date = new Date(timestamp.seconds * 1000);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } else if (timestamp.toDate) {
      const date = timestamp.toDate();
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
    return '';
  }
  function getWorkDuration(session) {
    if (session.adjustedWorkTime != null) {
      return formatTime(session.adjustedWorkTime);
    }
    if (session.startTime?.seconds && session.endTime?.seconds) {
      return formatTime(Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0)));
    }
    return '00:00';
  }
  function getPauseDuration(session) {
    return formatTime(session.totalPauseTime || 0);
  }
  // ...getZone, absences, etc. à ajouter comme dans AdminTimesheetValidation

  // Fonction pour découper l'absence sur la période affichée
  function getAbsenceOnWeek(abs, ws, we) {
    const absStart = abs.startDate ? new Date(abs.startDate) : null;
    const absEnd = abs.endDate ? new Date(abs.endDate) : null;
    if (!absStart || !absEnd) return null;
    if (absEnd < ws || absStart > we) return null;
    const start = absStart < ws ? ws : absStart;
    const end = absEnd > we ? we : absEnd;
    let total = 0;
    if (start.getTime() === end.getTime()) {
      if ((abs.startPart === 'am' && absStart >= ws) || (abs.endPart === 'matin' && absEnd <= we)) {
        total = 0.5;
      } else {
        total = 1;
      }
    } else {
      const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
      total = days;
      if (abs.startPart === 'am' && absStart >= ws) total -= 0.5;
      if (abs.endPart === 'matin' && absEnd <= we) total -= 0.5;
    }
    return {
      ...abs,
      displayStart: start,
      displayEnd: end,
      displayStartPart: absStart < ws ? '-' : (abs.startPart || '-'),
      displayEndPart: absEnd > we ? '-' : (abs.endPart || '-'),
      totalOnWeek: total
    };
  }

  // Fonction pour trouver le nom du site à partir d'une position
  function getZone(location) {
    if (!location || !location.latitude || !location.longitude) return 'HORS ZONE';
    const found = sites.find(site => {
      if (!site.latitude || !site.longitude || !site.rayon) return false;
      const dist = Math.sqrt(
        Math.pow(site.latitude - location.latitude, 2) +
        Math.pow(site.longitude - location.longitude, 2)
      ) * 111320; // approx. conversion deg -> m
      return dist <= site.rayon;
    });
    return found ? found.nom : 'HORS ZONE';
  }

  // Fonction de signature
  async function handleSign() {
    if (!signData || !employeeData) return;
    if (!sigCanvas || sigCanvas.isEmpty()) {
      alert('Merci de dessiner votre signature.');
      return;
    }

    try {
      setLoading(true);
      const signatureDataUrl = sigCanvas.getTrimmedCanvas().toDataURL('image/png');

      const docPdf = new jsPDF();
      docPdf.setFont("helvetica", "normal");

      const startY = generatePDFHeader(docPdf, signData, employeeData);
      const sessions = signData.sessions || [];
      let y = generateSessionsTable(docPdf, sessions, startY);

      // Titre du tableau des absences
      docPdf.setFontSize(13);
      docPdf.text('Absences et Congés', 14, y);
      y += 6;

      // Tableau des absences
      autoTable(docPdf, {
        startY: y,
        head: [["Type", "Date début", "Début", "Date fin", "Fin"]],
        body: (signData.absences || []).map(a => [
          a.type || '-',
          a.startDate || '-',
          a.startPart || '-',
          a.endDate || '-',
          a.endPart || '-'
        ]),
        theme: 'grid',
        styles: { fontSize: 10, fillColor: false },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] }
      });

      y = docPdf.lastAutoTable ? docPdf.lastAutoTable.finalY + 20 : y + 30;

      // **ORDRE CORRIGÉ** : Commentaires d'abord
      if (comment || signData.comment) {
        docPdf.setFontSize(10);
        docPdf.setFont("helvetica", "bold");
        docPdf.text("Commentaires Salarié :", 14, y);
        y += 6;
        docPdf.setFont("helvetica", "normal");
        const commentText = comment || signData.comment || "";
        const lines = docPdf.splitTextToSize(commentText, 180);
        docPdf.text(lines, 14, y);
        y += (lines.length * 5) + 10;
      }

      if (signData.employerComment) {
        docPdf.setFontSize(10);
        docPdf.setFont("helvetica", "bold");
        docPdf.text("Commentaires Employeur :", 14, y);
        y += 6;
        docPdf.setFont("helvetica", "normal");
        const lines = docPdf.splitTextToSize(signData.employerComment, 180);
        docPdf.text(lines, 14, y);
        y += (lines.length * 5) + 10;
      }
      
      // Vérifier si on a besoin d'une nouvelle page AVANT le bloc signature
      if (y > docPdf.internal.pageSize.height - 120) {
        docPdf.addPage();
        y = 20;
      }
      
      // **ORDRE CORRIGÉ** : Bloc signature APRÈS (version finale avec signature)
      y = generateSignatureBlock(docPdf, y, signData, false, signatureDataUrl);

      generatePDFFooter(docPdf);

      const pdfBlob = docPdf.output('blob');
      
      console.log('[DEBUG] Nom du fichier à uploader:', signData.employeeEmail, signData.monthStart);

      let pdfUrl = null;
      let fileName = null;
      let uploadError = null;

      try {
        // Upload du PDF sur R2
        console.log('[DEBUG] Début de l\'upload du PDF sur R2');
        
        const uploadResult = await uploadPdfToR2(
          pdfBlob, 
          signData.employeeEmail, 
          signData.monthStart
        );
        
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || "Échec de l'upload du PDF sur R2");
        }
        
        fileName = uploadResult.fileName;
        console.log('[DEBUG] Upload R2 réussi, génération URL signée...');
        
        // Générer une URL signée pour accéder au PDF
        pdfUrl = await generateSignedUrl(uploadResult.fileName);
        console.log('[DEBUG] URL signée générée:', pdfUrl);

      } catch (error) {
        console.error("[DEBUG] Erreur lors de l'upload du PDF sur R2:", error);
        uploadError = error;
        alert("Erreur lors de l'upload du PDF: " + error.message);
        throw error;
      }

      // Mise à jour de Firestore avec gestion d'erreur
      console.log('[DEBUG] Début de la mise à jour Firestore');
      const weekId = signData.id;
      const weekRef = doc(db, "workSessionsMonth", weekId);
      
      const updateData = {
        signed: true,
        status: "Signée",
        comment: comment || "",
        employerComment: signData.employerComment || null, // Conserver le commentaire employeur
        signature: signatureDataUrl,
        pdfUrl: pdfUrl || null,
        signedAt: new Date(),
        pdfFileName: fileName,
        updatedAt: new Date(),
        uploadError: uploadError ? uploadError.message : null,
        // Réinitialiser les notifications quand la feuille est signée
        timesheetNotificationVu: true,
        timesheetReminderCreated: false,
        reminderCreatedAt: null
      };

      await updateDoc(weekRef, updateData);
      console.log('[DEBUG] Firestore mis à jour avec succès');

      // Mettre à jour l'état pour afficher le PDF signé
      setSignData(prevData => ({ ...prevData, ...updateData }));
      setSigned(true);
      
    } catch (error) {
      console.error("[DEBUG] Erreur lors de la signature:", error);
      alert("Une erreur est survenue lors de la signature. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="dashboard-board-container" style={{
      minHeight: '100vh',
      background: `url(${loginBg}) center/cover no-repeat`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0
    }}>
      <div className="dashboard-board-content" style={{
        background: 'rgba(255,255,255,0.85)',
        borderRadius: 20,
        padding: 32,
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
        width: '90%',
        maxWidth: 800,
        minHeight: 340,
        textAlign: 'center'
      }}>
        <h2 style={{marginBottom: 8}}>Feuilles de Temps</h2>
        {employeeName && (
          <div style={{ fontSize: 16, color: '#555', marginBottom: 28 }}>{employeeName}</div>
        )}

        {loading ? (
          <div>Chargement...</div>
        ) : (
          <div style={{width: '100%', overflowX: 'auto'}}>
            <div className="weeks-grid">
              {weeks.map(week => {
                const statusKey = getWeekStatus(week);
                const status = statusInfo[statusKey];
                
                return (
                  <div key={week.id} className="week-button-wrapper">
                    <button
                      className="week-button"
                      onClick={() => {
                        setModalData(week);
                      }}
                    >
                      <div className="week-button-main-content">
                        <span className="week-button-label">{week.monthName || 'Mois'}</span>
                        <span className="week-button-date">{week.year || ''}</span>
                      </div>
                      <div className="week-button-status-vignette" style={{ backgroundColor: status.color }}>
                        {status.text}
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: 16, width: '100%', marginTop: 32}}>
          <button className="dashboard-back" onClick={() => navigate("/dashboard-board")}>Retour</button>
        </div>
      </div>

      {/* Vue de détail de la semaine */}
      {modalData && (
        <div className="week-detail-modal">
          <div className="week-detail-content">
            <button 
              className="close-detail-button"
              onClick={() => {
                setModalData(null);
                setModalOpen(false);
              }}
            >
              ×
            </button>
            
            <h3>{modalData.monthName} {modalData.year}</h3>
            
            <div className="week-detail-status">
              <span 
                className="status-badge"
                style={{ backgroundColor: statusInfo[getWeekStatus(modalData)].color }}
              >
                {statusInfo[getWeekStatus(modalData)].text}
              </span>
            </div>

            <div className="week-detail-actions">
              <button 
                className="action-button view-button"
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                VOIR
              </button>
              
              {/* Affichage conditionnel des boutons selon le statut */}
              {getWeekStatus(modalData) === 'waitingSignature' && (
                <button 
                  className="action-button sign-button"
                  onClick={async () => {
                    const freshData = await reloadWeekData(modalData.id);
                    setSignData(freshData || modalData);
                    setSignModalOpen(true);
                    setModalData(null);
                  }}
                >
                  SIGNER
                </button>
              )}
              
              {getWeekStatus(modalData) === 'signed' && modalData.pdfUrl && (
                <a 
                  href={modalData.pdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="action-button pdf-button"
                >
                  PDF
                </a>
              )}
              {/* Pour le statut 'waitingEmployer', aucun autre bouton que 'VOIR' n'est affiché */}
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="timesheet-actions">
        {/* Popup Voir - contenu dynamique sessions + absences */}
        <ReactModal
          isOpen={modalOpen}
          onRequestClose={() => {
            setModalOpen(false);
          }}
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              zIndex: 1000
            },
            content: {
              position: 'absolute',
              top: '5%',
              left: '5%',
              right: '5%',
              bottom: '5%',
              width: '90%',
              height: '90%',
              maxWidth: 'none',
              maxHeight: 'none',
              margin: '0',
              padding: '20px',
              borderRadius: '10px',
              overflow: 'auto',
              backgroundColor: 'white'
            }
          }}
          className="modal-responsive"
        >
          <div style={{ marginBottom: '20px' }}>
            <h3>Détail du mois</h3>
            <p>{modalData?.monthName} {modalData?.year}</p>
            <p style={{fontSize: '12px', color: '#666'}}>Du {formatDateSafely(modalData?.monthStart)} au {formatDateSafely(modalData?.monthEnd)}</p>
          </div>

          <div style={{ marginBottom: '20px', overflowX: 'auto', width: '100%' }}>
            <div className="table-container">
              <h4>Sessions de travail</h4>
              <table className="session-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th>Temps de Travail</th>
                    <th>Pause</th>
                    <th>Zone Début</th>
                    <th>Zone Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, idx) => (
                    <tr key={s.id || idx}>
                      <td>{s.startTime?.seconds ? new Date(s.startTime.seconds * 1000).toLocaleDateString('fr-FR') : '-'}</td>
                      <td>{formatTimeHHMM(s.startTime)}</td>
                      <td>{formatTimeHHMM(s.endTime)}</td>
                      <td>{getWorkDuration(s)}</td>
                      <td>{getPauseDuration(s)}</td>
                      <td>{getZone(s.startLocation)}</td>
                      <td>{getZone(s.endLocation)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f5f5f5', fontWeight: 'bold' }}>
                    <td colSpan={3}>Total</td>
                    <td>{formatTime(sessions.reduce((acc, s) => {
                      const sessionWorkTimeMs = s.adjustedWorkTime != null 
                        ? s.adjustedWorkTime 
                        : (s.startTime?.seconds && s.endTime?.seconds 
                          ? Math.max(0, (s.endTime.seconds - s.startTime.seconds) * 1000 - (s.totalPauseTime || 0)) 
                          : 0);
                      // Arrondir chaque ligne comme dans l'affichage individuel
                      const roundedWorkTimeMs = Math.round(sessionWorkTimeMs / 60000) * 60000;
                      return acc + roundedWorkTimeMs;
                    }, 0))}</td>
                    <td>{formatTime(sessions.reduce((acc, s) => {
                      const sessionPauseTimeMs = s.totalPauseTime || 0;
                      // Arrondir chaque ligne comme dans l'affichage individuel
                      const roundedPauseTimeMs = Math.round(sessionPauseTimeMs / 60000) * 60000;
                      return acc + roundedPauseTimeMs;
                    }, 0))}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginBottom: '20px', overflowX: 'auto', width: '100%' }}>
            <div className="table-container">
              <h4>Absences</h4>
              <table className="session-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date début</th>
                    <th>Début</th>
                    <th>Date fin</th>
                    <th>Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.map((a, idx) => {
                    const absOnWeek = getAbsenceOnWeek(a, parseISODate(modalData?.monthStart), parseISODate(modalData?.monthEnd));
                    if (!absOnWeek) return null;
                    return (
                      <tr key={a.id || idx}>
                        <td>{a.type || '-'}</td>
                        <td>{absOnWeek.displayStart.toLocaleDateString('fr-FR')}</td>
                        <td>{absOnWeek.displayStartPart}</td>
                        <td>{absOnWeek.displayEnd.toLocaleDateString('fr-FR')}</td>
                        <td>{absOnWeek.displayEndPart}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {modalData?.employerComment && (
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
              <h4 style={{ marginBottom: '10px' }}>Commentaires Employeur :</h4>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{modalData.employerComment}</p>
            </div>
          )}

          <button
            onClick={() => setModalOpen(false)}
            style={{
              marginTop: '20px',
              padding: '8px 16px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Fermer
          </button>
        </ReactModal>
        {/* Popup Signature */}
        <ReactModal
          isOpen={signModalOpen}
          onRequestClose={() => setSignModalOpen(false)}
          style={{ 
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              zIndex: 1000
            },
            content: { 
              position: 'absolute',
              top: '5%',
              left: '5%',
              right: '5%',
              bottom: '5%',
              width: '90%',
              height: '90%',
              maxWidth: 'none',
              maxHeight: 'none',
              margin: '0',
              padding: '24px',
              borderRadius: 12,
              overflow: 'auto',
              backgroundColor: 'white'
            } 
          }}
          className="modal-responsive"
          ariaHideApp={false}
        >
          {signData && (
            <div>
              <h2>{signed ? 'Feuille signée' : 'Signature de la feuille de temps'}</h2>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{employeeName}</div>
              <div style={{ marginBottom: 16 }}>{signData.monthName} {signData.year}</div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 16 }}>Du {formatDateSafely(signData.monthStart)} au {formatDateSafely(signData.monthEnd)}</div>
              
              {signed ? (
                <div>
                  <div className="pdf-container">
                    <iframe 
                      src={signData.pdfUrl} 
                      title="PDF signé" 
                      className="pdf-iframe"
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginTop: '20px' }}>
                    <a 
                      href={signData.pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="admin-button"
                      style={{ textDecoration: 'none' }}
                    >
                      Voir PDF
                    </a>
                    <button 
                      className="admin-button"
                      onClick={() => setSignModalOpen(false)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* PDF généré à afficher ici (prévisualisation) */}
                  <div className="pdf-container">
                    {pdfPreviewUrl ? (
                      <iframe 
                        src={pdfPreviewUrl} 
                        title="Prévisualisation PDF" 
                        className="pdf-iframe"
                      />
                    ) : (
                      <div style={{ color: '#888', fontStyle: 'italic' }}>[PDF généré à venir]</div>
                    )}
                  </div>
                  
                  {/* ORDRE CORRIGÉ : Commentaire AVANT signature */}
                  <div style={{ marginBottom: 20, marginTop: 20 }}>
                    <label>Commentaire / Réserves (facultatif) :</label>
                    <textarea value={comment} onChange={e => setComment(e.target.value)} style={{ width: '100%', minHeight: 50, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, padding: 8 }} />
                  </div>

                  {/* Champ de signature manuscrite */}
                  <div style={{ marginBottom: 12 }}>
                    <label>Signature manuscrite :</label>
                    <div className="signature-container">
                      <SignatureCanvas
                        penColor="black"
                        canvasProps={{ className: 'sigCanvas' }}
                        ref={ref => setSigCanvas(ref)}
                        backgroundColor="#fff"
                      />
                    </div>
                    <button className="admin-button" style={{ marginTop: 8 }} onClick={() => sigCanvas && sigCanvas.clear()}>Effacer</button>
                  </div>

                  {/* Slider pour valider la signature */}
                  <div style={{ margin: '24px 0', textAlign: 'center' }}>
                    <div 
                      style={{
                        width: '100%',
                        height: 60,
                        background: '#f5f5f5',
                        borderRadius: 30,
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (!slider) {
                          setSlider(true);
                          handleSign();
                        }
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: slider ? 'calc(100% - 60px)' : '0',
                          top: 0,
                          width: 60,
                          height: 60,
                          background: slider ? '#4CAF50' : '#2196F3',
                          borderRadius: '50%',
                          transition: 'left 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: 24
                        }}
                      >
                        {slider ? '✓' : '→'}
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#666',
                          fontSize: 16,
                          pointerEvents: 'none'
                        }}
                      >
                        {slider ? 'Signature validée' : 'Glisser pour signer'}
                      </div>
                    </div>
                  </div>
                  {loading && (
                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                      <div style={{ color: '#666' }}>Génération du PDF en cours...</div>
                      <div style={{ marginTop: 10 }}>⏳</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                    <button className="admin-button" onClick={() => setSignModalOpen(false)}>Fermer</button>
                  </div>
                </>
              )}
            </div>
          )}
        </ReactModal>
      </div>
    </div>
  );
}

export default EmployeeTimesheet;